<template>
  <ReportLayoutModerno
    id="conferenceList"
    :prefecture="prefecture"
    :period="convertDate(filter.startDate) + ' até ' + convertDate(filter.endDate)"
  >
    <template v-slot:icon>mdi-check-all</template>
    <template v-slot:title>RELATÓRIO DE CONFERÊNCIA</template>
    <template v-slot:sub-header>
      <h3 class="text-sm font-bold text-slate-600">
        Fornecedor: <span class="text-orange-600 uppercase">{{ provider && provider.provider_name ? provider.provider_name : '' }}</span>
      </h3>
    </template>

    <!-- LISTAGEM POR SECRETARIA -->
    <div v-for="(d, i) in data.secretariats" :key="i" class="mb-10 break-inside-avoid shadow-sm border border-slate-200 rounded-2xl overflow-hidden">
      <!-- CABEÇALHO DA SECRETARIA -->
      <div class="bg-slate-800 text-white px-8 py-4 flex items-center justify-between">
        <div class="flex flex-col">
          <span class="text-[9px] font-black text-slate-400 uppercase tracking-[0.2em]">SECRETARIA</span>
          <span class="text-sm font-bold uppercase tracking-wide">{{ d.sec_name }}</span>
        </div>
        <v-icon color="white/20" size="32">mdi-office-building</v-icon>
      </div>

      <!-- TABELA DE VEÍCULOS -->
      <div class="overflow-x-auto">
        <table class="w-full text-left border-collapse">
          <thead>
            <tr class="bg-slate-50 border-b border-slate-200">
              <th class="p-4 text-[9px] font-black text-slate-500 uppercase tracking-wider">Subsetor</th>
              <th class="p-4 text-[9px] font-black text-slate-500 uppercase tracking-wider">Veículo</th>
              <th class="p-4 text-[9px] font-black text-slate-500 uppercase tracking-wider">Modelo</th>
              <th class="p-4 text-[9px] font-black text-slate-500 uppercase tracking-wider w-32">Placa</th>
              <th class="p-4 text-[9px] font-black text-slate-500 uppercase tracking-wider">Combustível</th>
              <th class="p-4 text-[9px] font-black text-slate-500 uppercase tracking-wider text-center">Abastecimentos</th>
              <th class="p-4 text-[9px] font-black text-slate-500 uppercase tracking-wider text-right w-32">Litros</th>
              <th class="p-4 text-[9px] font-black text-slate-500 uppercase tracking-wider text-right w-32">Total</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(vehicle, ix) in d.vehicles" :key="ix" class="border-b border-slate-100 last:border-0 hover:bg-slate-50 transition-colors">
              <td class="p-4 text-xs font-medium text-slate-600 uppercase">{{ vehicle.subsec_desc || '---' }}</td>
              <td class="p-4 text-xs font-bold text-slate-700 uppercase">{{ vehicle.brand_desc }}</td>
              <td class="p-4 text-xs font-medium text-slate-600 uppercase">{{ vehicle.model_desc }}</td>
              <td class="p-4 text-xs font-black text-orange-600 uppercase tracking-wider">{{ vehicle.vehicle_board }}</td>
              <td class="p-4 text-xs font-bold text-slate-700 uppercase">{{ vehicle.item_description_desc }}</td>
              <td class="p-4 text-[9px] text-slate-400 font-medium max-w-xs break-all text-center">
                {{ vehicle.coupons ? vehicle.coupons.toString() : '' }}
              </td>
              <td class="p-4 text-xs font-black text-slate-800 text-right">{{ vehicle.totalLiters ? realMaskII(vehicle.totalLiters.toFixed(3)) : '0,000' }}</td>
              <td class="p-4 text-xs font-black text-slate-900 text-right font-mono">R$ {{ vehicle.totalValue ? realMask(vehicle.totalValue.toFixed(2)) : '0,00' }}</td>
            </tr>
          </tbody>
          <!-- SUBTOTAL DA SECRETARIA -->
          <tfoot class="bg-slate-50/80 font-black text-slate-800 border-t border-slate-200">
            <tr>
              <td colspan="5" class="p-4 text-[10px] uppercase text-right text-slate-400 tracking-widest">SUBTOTAL {{ d.sec_name }}:</td>
              <td></td>
              <td class="p-4 text-sm text-right text-slate-900">{{ d.totalLiters ? realMask(Number(d.totalLiters).toFixed(2)) : 0 }} Lts</td>
              <td class="p-4 text-sm text-right text-slate-900 font-mono">R$ {{ d.totalValue ? realMask(Number(d.totalValue).toFixed(2)) : 0 }}</td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>

    <!-- RESUMOS FINAIS -->
    <div class="mt-16 break-inside-avoid p-8 bg-slate-900 rounded-3xl text-white shadow-2xl relative overflow-hidden">
      <v-icon color="white/5" size="180" class="absolute -right-10 -bottom-10 pointer-events-none">mdi-finance</v-icon>
      
      <h2 class="text-xl font-black text-white uppercase tracking-[0.3em] text-center mb-10 pb-4 border-b border-white/10">RESUMO CONSOLIDADO POR COMBUSTÍVEL</h2>
      
      <div class="grid grid-cols-1 md:grid-cols-2 gap-10">
        <!-- RESUMO PREÇO LICITADO -->
        <div class="bg-white/5 rounded-2xl border border-white/10 overflow-hidden">
          <div class="bg-indigo-600/30 px-4 py-3 text-center border-b border-white/10">
            <span class="text-[10px] font-black text-indigo-200 uppercase tracking-[0.2em]">RESUMO PREÇO LICITADO</span>
          </div>
          <table class="w-full text-[11px] text-left">
            <thead>
              <tr class="text-slate-400 uppercase font-black tracking-widest">
                <th class="p-4">Tipo</th>
                <th class="p-4 text-right">Preço</th>
                <th class="p-4 text-right">Total Lts</th>
                <th class="p-4 text-right">Total Valor</th>
              </tr>
            </thead>
            <tbody>
              <template v-for="(resume, idx) in data.finalTotals.totalTypes">
                <tr v-if="resume.name" :key="idx" class="border-t border-white/5">
                  <td class="p-4 font-bold uppercase">{{ resume.name }}</td>
                  <td class="p-4 text-right text-slate-300 font-mono">{{ resume.item_billing_price ? realMaskII(resume.item_billing_price.toFixed(3)) : 0 }}</td>
                  <td class="p-4 text-right font-black">{{ resume.liters ? realMask((resume.liters).toFixed(2)) : 0 }}</td>
                  <td class="p-4 text-right font-black text-orange-400 font-mono text-xs">R$ {{ resume.valueBilling ? realMask((resume.valueBilling).toFixed(2)) : 0 }}</td>
                </tr>
              </template>
            </tbody>
          </table>
        </div>

        <!-- RESUMO PREÇO DIA -->
        <div class="bg-white/5 rounded-2xl border border-white/10 overflow-hidden">
          <div class="bg-orange-600/30 px-4 py-3 text-center border-b border-white/10">
            <span class="text-[10px] font-black text-orange-200 uppercase tracking-[0.2em]">RESUMO PREÇO DIA</span>
          </div>
          <table class="w-full text-[11px] text-left">
            <thead>
              <tr class="text-slate-400 uppercase font-black tracking-widest">
                <th class="p-4">Tipo</th>
                <th class="p-4 text-right">Preço Dia</th>
                <th class="p-4 text-right">Total Lts</th>
                <th class="p-4 text-right">Total Valor</th>
              </tr>
            </thead>
            <tbody>
              <template v-for="(resume, idx) in data.finalTotals.totalTypes">
                <tr v-if="resume.name" :key="idx" class="border-t border-white/5">
                  <td class="p-4 font-bold uppercase">{{ resume.name }}</td>
                  <td class="p-4 text-right text-slate-300 font-mono">{{ resume.supply_price_day ? realMaskII(resume.supply_price_day.toFixed(3)) : 0 }}</td>
                  <td class="p-4 text-right font-black">{{ resume.liters ? realMask((resume.liters).toFixed(2)) : 0 }}</td>
                  <td class="p-4 text-right font-black text-indigo-400 font-mono text-xs">R$ {{ resume.valueDay ? realMask((resume.valueDay).toFixed(2)) : 0 }}</td>
                </tr>
              </template>
            </tbody>
          </table>
        </div>
      </div>

      <!-- TOTAIS GERAIS -->
      <div class="mt-10 pt-10 border-t border-white/10 flex flex-col md:flex-row items-center justify-between gap-10">
         <div class="flex flex-col items-center md:items-start">
            <span class="text-[9px] font-black text-slate-400 uppercase tracking-[0.3em] mb-2">VOLUME TOTAL ACUMULADO</span>
            <span class="text-3xl font-black text-white tracking-tighter">{{ data.finalTotals.totalFinalLiters ? realMask(data.finalTotals.totalFinalLiters.toFixed(2)) : 0 }} <span class="text-sm font-medium text-slate-500">LITROS</span></span>
         </div>
         
         <div class="bg-white/5 p-6 rounded-2xl border border-white/10 flex flex-col items-center md:items-end min-w-[300px]">
             <span class="text-[9px] font-black text-orange-500 uppercase tracking-[0.3em] mb-2">VALOR TOTAL DO PERÍODO</span>
             <span class="text-4xl font-black text-white font-mono tracking-tighter uppercase">
               <span class="text-lg text-slate-500 font-medium mr-2">R$</span>
               {{ data.finalTotals.totalFinalValuesBilling ? realMask(data.finalTotals.totalFinalValuesBilling.toFixed(2)) : 0 }}
             </span>
         </div>
      </div>
    </div>
  </ReportLayoutModerno>
</template>

<script>
import helpers from "../../../mixins/helpers";
import ReportLayoutModerno from "@/components/ReportLayoutModerno.vue";

export default {
  components: { ReportLayoutModerno },
  props: ["data", "filter", "prefecture", "provider"],
  mixins: [helpers],
  methods: {
    initialize() {
      this.helpers;
    },
  },
};
</script>

<style></style>